import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import TokenChart from '../coinInfo/tokenChart';

// ---------------------------------------------------------------------------
// Mini candlestick chart for the "Market Stats" column of the listing tables
// (replaces the area sparkline). Last 24 hourly candles from
// /hootdex/candles - the same OHLC source as the full chart on the token
// page and the exchange - drawn as plain SVG so 25 rows stay cheap.
// Loads only once the row scrolls into view and refreshes every minute.
// Falls back to the old sparkline when a token has no candles yet.
// ---------------------------------------------------------------------------

const W = 150;
const H = 50;
const N = 24;
const UP = '#34D399';
const DOWN = '#F05D6C';

export default function MiniCandles({ symbol, chartSymbol }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [candles, setCandles] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setVisible(true), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !symbol) return undefined;
    let cancelled = false;
    let timer;
    const load = () =>
      api
        .get('/hootdex/candles', { params: { symbol, chartSymbol, interval: '1h' }, timeout: 45000 })
        .then((res) => {
          if (cancelled) return;
          const c = Array.isArray(res.data?.candles) ? res.data.candles : [];
          setCandles((prev) => (c.length ? c.slice(-N) : prev || []));
        })
        .catch(() => !cancelled && setCandles((prev) => prev || []))
        .finally(() => {
          if (!cancelled) timer = setTimeout(load, 60000);
        });
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [visible, symbol, chartSymbol]);

  if (candles && candles.length === 0) {
    // Nothing traded / no price history yet: keep the old sparkline.
    return <TokenChart symbol={chartSymbol || symbol} />;
  }

  let body = null;
  if (candles && candles.length) {
    const hi = Math.max(...candles.map((c) => c.high));
    const lo = Math.min(...candles.map((c) => c.low));
    const span = hi - lo || hi || 1;
    const y = (v) => 2 + (1 - (v - lo) / span) * (H - 4);
    const step = W / N;
    const bw = Math.max(1.5, step * 0.6);
    const offset = (N - candles.length) * step;
    const first = candles[0].open;
    const last = candles[candles.length - 1].close;
    const pct = first ? ((last - first) / first) * 100 : 0;
    body = (
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`24h candles, ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`}>
        <title>{`Last 24h (hourly): ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`}</title>
        {candles.map((c, i) => {
          const up = c.close >= c.open;
          const color = up ? UP : DOWN;
          const cx = offset + i * step + step / 2;
          const top = y(Math.max(c.open, c.close));
          const bot = y(Math.min(c.open, c.close));
          return (
            <g key={c.time}>
              <line x1={cx} x2={cx} y1={y(c.high)} y2={y(c.low)} stroke={color} strokeWidth={1} />
              <rect x={cx - bw / 2} y={top} width={bw} height={Math.max(1, bot - top)} fill={color} rx={0.5} />
            </g>
          );
        })}
      </svg>
    );
  }

  return (
    <div ref={ref} style={{ width: W, height: H, display: 'flex', alignItems: 'center', margin: '4px 0 10px', position: 'relative', zIndex: 0 }}>
      {body}
    </div>
  );
}
